/**
 * AdminConsole — minimal Administrator/Estimator surface for complete-on-behalf.
 *
 * Search customers by name/address, then start a quote on their behalf: this
 * routes into the normal design flow carrying the masterId, and the resulting
 * project/estimate is owned by the customer (so it lands in their portal). Only
 * ADMINISTRATOR/ESTIMATOR reach this screen (gated by /me role in the router
 * guard below); the backend also 403s non-staff.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminSearchMasters, getMe, type Master } from '../lib/api';

export function AdminConsole(): JSX.Element {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Master[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    getMe()
      .then((me) => setAllowed(me.role === 'ADMINISTRATOR' || me.role === 'ESTIMATOR'))
      .catch(() => setAllowed(false));
  }, []);

  async function search(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setError(null);
    try {
      setResults(await adminSearchMasters(query.trim()));
    } catch {
      setError('Search failed. Try again.');
    } finally {
      setSearching(false);
    }
  }

  if (allowed === null) {
    return (
      <div className="flow flow--single">
        <div className="stage-card"><p className="stage-card__lede">Checking access&hellip;</p></div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <h1>Not available</h1>
          <p className="stage-card__lede">This area is for staff only.</p>
          <div className="q-actions">
            <button type="button" className="pb-btn" onClick={() => navigate('/dashboard')}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>Staff console</h1>
        <p className="stage-card__lede">Review submitted leads, or find a customer to quote on their behalf.</p>

        <form onSubmit={search} style={{ display: 'flex', gap: 'var(--pb-2)', marginTop: 'var(--pb-3)' }}>
          <input
            className="pb-field"
            placeholder="Name, street, city, or ZIP"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" className="pb-btn" disabled={searching || !query.trim()}>
            {searching ? 'Searching\u2026' : 'Search'}
          </button>
        </form>

        {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

        <div style={{ marginTop: 'var(--pb-3)' }}>
          {results.map((m) => (
            <div key={m.id} className="pb-surface" style={{ marginBottom: 'var(--pb-2)' }}>
              <div className="pb-option__label">{m.fullName}</div>
              <div className="estimate__hint">
                {m.addressLine1}, {m.city} {m.state} {m.postalCode}
                {m.phone ? ` \u00b7 ${m.phone}` : ''}
              </div>
              <div className="q-actions" style={{ marginTop: 'var(--pb-2)' }}>
                <button
                  type="button"
                  className="pb-btn"
                  onClick={() => navigate('/service', { state: { oboMasterId: m.id } })}
                >
                  Quote on their behalf
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
